import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Buildings, Car, CheckCircle, Phone } from '@phosphor-icons/react';
import { useTheme } from '../contexts/ThemeContext';
import { useSiteConfig } from '../contexts/SiteConfigContext';

const HERO_OFFICE = "https://static.prod-images.emergentagent.com/jobs/7f6009de-6ead-4623-a7dd-0d5a747b70b6/images/522328254da0ffab80cdb5ba726951720f50569037d20dbc282cade8d1e57ca7.png";
const HERO_CAR = "https://static.prod-images.emergentagent.com/jobs/7f6009de-6ead-4623-a7dd-0d5a747b70b6/images/2bf093a065765327fe9434fe03540a47aa5f13735d6b471590a38e83ba941b58.png";

const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
};

const staggerContainer = {
    animate: {
        transition: {
            staggerChildren: 0.1
        }
    }
};

export default function HomePage() {
    const { theme } = useTheme();
    const { config } = useSiteConfig();
    
    const LOGO_GLOBAL = config.logo_global;
    const LOGO_LUXURY = config.logo_luxury;

    const advantages = [
        { icon: CheckCircle, title: "Qualité Premium", desc: "Produits et véhicules sélectionnés avec soin" },
        { icon: CheckCircle, title: "Service Personnalisé", desc: "Accompagnement dédié à chaque client" },
        { icon: CheckCircle, title: "Prix Compétitifs", desc: "Meilleurs rapports qualité-prix du marché" },
        { icon: CheckCircle, title: "Garantie Assurée", desc: "Satisfaction garantie sur tous nos produits" }
    ];

    return (
        <div data-testid="home-page">
            {/* Hero Section */}
            <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
                <div className="absolute inset-0">
                    <div className={`absolute inset-0 ${theme === 'dark' ? 'bg-gradient-to-r from-black/80 via-black/60 to-transparent' : 'bg-gradient-to-r from-white/90 via-white/70 to-transparent'} z-10`} />
                    <img 
                        src={theme === 'dark' ? HERO_CAR : HERO_OFFICE} 
                        alt="Hero" 
                        className="w-full h-full object-cover"
                    />
                </div>
                
                <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <motion.div 
                        initial="initial"
                        animate="animate"
                        variants={staggerContainer}
                        className="max-w-2xl"
                    >
                        <motion.p 
                            variants={fadeInUp}
                            className={`text-sm font-medium tracking-[0.2em] uppercase mb-4 ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`}
                        >
                            MMD Global Enterprise
                        </motion.p>
                        <motion.h1 
                            variants={fadeInUp}
                            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter mb-6"
                        >
                            {config?.hero_title || "Connecting Quality to the World"}
                        </motion.h1>
                        <motion.p 
                            variants={fadeInUp}
                            className={`text-base sm:text-lg leading-relaxed mb-8 ${theme === 'dark' ? 'text-mmd-silver/90' : 'text-mmd-navy/80'}`}
                        >
                            {config?.hero_subtitle || "Votre partenaire de confiance pour le matériel de bureau professionnel et l'importation de véhicules de luxe au Sénégal."}
                        </motion.p>
                        <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
                            <Link
                                to="/materiel"
                                data-testid="hero-cta-materiel"
                                className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 transform hover:-translate-y-1 ${
                                    theme === 'dark' 
                                        ? 'bg-mmd-gold text-mmd-dark-bg hover:bg-mmd-gold-hover' 
                                        : 'bg-mmd-navy text-white hover:bg-mmd-navy-hover'
                                }`}
                            >
                                <Buildings size={20} />
                                Matériel de Bureau
                            </Link>
                            <Link
                                to="/voitures"
                                data-testid="hero-cta-voitures"
                                className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium border transition-all duration-200 transform hover:-translate-y-1 ${
                                    theme === 'dark' 
                                        ? 'border-mmd-gold text-mmd-gold hover:bg-mmd-gold/10' 
                                        : 'border-mmd-navy text-mmd-navy hover:bg-mmd-navy/10'
                                }`}
                            >
                                <Car size={20} />
                                Voitures de Luxe
                            </Link>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* About Section */}
            <section className={`py-20 ${theme === 'dark' ? 'bg-mmd-surface-dark' : 'bg-white'}`} data-testid="about-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <p className={`text-sm font-medium tracking-[0.2em] uppercase mb-4 ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`}>
                                Qui sommes-nous
                            </p>
                            <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight mb-6">
                                Deux expertises, une seule mission
                            </h2>
                            <p className={`leading-relaxed mb-6 ${theme === 'dark' ? 'text-mmd-silver/80' : 'text-mmd-navy/70'}`}>
                                {config?.mission_text || "MMD Global Enterprise est votre partenaire de confiance pour équiper votre espace de travail avec du matériel professionnel de qualité et pour importer des véhicules de luxe au Sénégal."}
                            </p>
                            <p className={`leading-relaxed ${theme === 'dark' ? 'text-mmd-silver/80' : 'text-mmd-navy/70'}`}>
                                {config?.vision_text || "Avec MMD Luxury Cars, nous vous offrons l'accès aux plus belles marques automobiles du monde, importées directement pour vous avec un service personnalisé et des garanties solides."}
                            </p>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="grid grid-cols-2 gap-6"
                        >
                            <div className={`p-6 rounded-2xl ${theme === 'dark' ? 'bg-mmd-dark-bg border border-white/10' : 'bg-mmd-light-bg border border-black/5'}`}>
                                <img src={LOGO_GLOBAL} alt="MMD Global" className="h-20 w-20 rounded-full object-cover mx-auto mb-4" />
                                <h3 className="font-heading font-semibold text-center mb-2">MMD Global</h3>
                                <p className={`text-sm text-center ${theme === 'dark' ? 'text-mmd-silver/70' : 'text-mmd-navy/60'}`}>
                                    Matériel de bureau
                                </p>
                            </div>
                            <div className={`p-6 rounded-2xl ${theme === 'dark' ? 'bg-mmd-dark-bg border border-white/10' : 'bg-mmd-light-bg border border-black/5'}`}>
                                <img src={LOGO_LUXURY} alt="MMD Luxury Cars" className="h-20 w-20 rounded-full object-cover mx-auto mb-4" />
                                <h3 className="font-heading font-semibold text-center mb-2">MMD Luxury Cars</h3>
                                <p className={`text-sm text-center ${theme === 'dark' ? 'text-mmd-silver/70' : 'text-mmd-navy/60'}`}>
                                    Voitures importées
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Activities Section */}
            <section className={`py-20 ${theme === 'dark' ? 'bg-mmd-dark-bg' : 'bg-mmd-light-bg'}`} data-testid="activities-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <p className={`text-sm font-medium tracking-[0.2em] uppercase mb-4 ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`}>
                            Nos Activités
                        </p>
                        <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight">
                            Deux domaines d'excellence
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Office Equipment Card */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="group relative overflow-hidden rounded-2xl"
                        >
                            <div className="aspect-[4/3] overflow-hidden">
                                <img 
                                    src={HERO_OFFICE} 
                                    alt={config.global_title || "Matériel de bureau"} 
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                            </div>
                            <div className={`absolute inset-0 ${theme === 'dark' ? 'bg-gradient-to-t from-black/90 via-black/50 to-transparent' : 'bg-gradient-to-t from-mmd-navy/90 via-mmd-navy/50 to-transparent'}`} />
                            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                                {config.global_subtitle && (
                                    <p className="text-xs font-medium tracking-[0.2em] uppercase text-mmd-gold mb-2">
                                        {config.global_subtitle}
                                    </p>
                                )}
                                <h3 className="font-heading text-2xl font-semibold text-white mb-2">
                                    {config.global_title || "Matériel de Bureau"}
                                </h3>
                                <p className="text-white/80 text-sm mb-4">
                                    {config.global_description || "Bureaux, fauteuils, imprimantes et accessoires de qualité professionnelle."}
                                </p>
                                <Link 
                                    to="/materiel"
                                    data-testid="activity-link-materiel"
                                    className="inline-flex items-center gap-2 text-mmd-gold font-medium hover:gap-3 transition-all"
                                >
                                    Découvrir <ArrowRight size={18} />
                                </Link>
                            </div>
                        </motion.div>

                        {/* Cars Card */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="group relative overflow-hidden rounded-2xl"
                        >
                            <div className="aspect-[4/3] overflow-hidden">
                                <img 
                                    src={HERO_CAR} 
                                    alt={config.luxury_title || "Voitures de luxe"} 
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                                {config.luxury_subtitle && (
                                    <p className="text-xs font-medium tracking-[0.2em] uppercase text-mmd-gold mb-2">
                                        {config.luxury_subtitle}
                                    </p>
                                )}
                                <h3 className="font-heading text-2xl font-semibold text-white mb-2">
                                    {config.luxury_title || "Voitures de Luxe"}
                                </h3>
                                <p className="text-white/80 text-sm mb-4">
                                    {config.luxury_description || "Importation de véhicules haut de gamme des meilleures marques mondiales."}
                                </p>
                                <Link 
                                    to="/voitures"
                                    data-testid="activity-link-voitures"
                                    className="inline-flex items-center gap-2 text-mmd-gold font-medium hover:gap-3 transition-all"
                                >
                                    Découvrir <ArrowRight size={18} />
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Advantages Section */}
            <section className={`py-20 ${theme === 'dark' ? 'bg-mmd-surface-dark' : 'bg-white'}`} data-testid="advantages-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <p className={`text-sm font-medium tracking-[0.2em] uppercase mb-4 ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`}>
                            Pourquoi nous choisir
                        </p>
                        <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight">
                            Nos avantages
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {advantages.map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className={`p-6 rounded-xl border ${theme === 'dark' ? 'border-white/10 hover:border-mmd-gold/50' : 'border-black/5 hover:border-mmd-navy/30'} transition-colors`}
                            >
                                <item.icon size={32} className={`mb-4 ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`} weight="fill" />
                                <h3 className="font-heading font-semibold mb-2">{item.title}</h3>
                                <p className={`text-sm ${theme === 'dark' ? 'text-mmd-silver/70' : 'text-mmd-navy/60'}`}>
                                    {item.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className={`py-20 ${theme === 'dark' ? 'bg-mmd-dark-bg' : 'bg-mmd-light-bg'}`} data-testid="cta-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className={`rounded-2xl p-8 sm:p-12 text-center ${theme === 'dark' ? 'bg-gradient-to-br from-mmd-gold/20 to-mmd-gold/5 border border-mmd-gold/30' : 'bg-gradient-to-br from-mmd-navy/10 to-mmd-navy/5 border border-mmd-navy/20'}`}>
                        <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight mb-4">
                            Prêt à démarrer votre projet ?
                        </h2>
                        <p className={`mb-8 max-w-xl mx-auto ${theme === 'dark' ? 'text-mmd-silver/80' : 'text-mmd-navy/70'}`}>
                            Contactez-nous pour discuter de vos besoins en matériel de bureau ou pour découvrir notre sélection de véhicules.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <Link
                                to="/contact"
                                data-testid="cta-contact"
                                className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 transform hover:-translate-y-1 ${
                                    theme === 'dark' 
                                        ? 'bg-mmd-gold text-mmd-dark-bg hover:bg-mmd-gold-hover' 
                                        : 'bg-mmd-navy text-white hover:bg-mmd-navy-hover'
                                }`}
                            >
                                Nous contacter
                                <ArrowRight size={18} />
                            </Link>
                            <a
                                href="tel:+221768462040"
                                data-testid="cta-phone"
                                className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium border transition-all duration-200 ${
                                    theme === 'dark' 
                                        ? 'border-mmd-gold text-mmd-gold hover:bg-mmd-gold/10' 
                                        : 'border-mmd-navy text-mmd-navy hover:bg-mmd-navy/10'
                                }`}
                            >
                                <Phone size={18} />
                                +221 76 846 20 40
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
